import * as tf from "@tensorflow/tfjs"
import "@tensorflow/tfjs-backend-webgpu"
import { WebGPUBackend } from "@tensorflow/tfjs-backend-webgpu"
import { isWebGPUSupported } from "@tensorflow/tfjs-backend-webgpu/dist/webgpu_util"
import { Sentry } from "@/telemetry/sentry"
import { retry } from "@/utils/retry"

export * from "@tensorflow/tfjs"

interface LoadGraphModelOptions {
	onProgress?: (progress: number) => void
	streamWeights?: boolean
}

type PerfSnapshotProvider = () => {
	totals?: Record<string, number>
	counters?: Record<string, number>
	alive?: Record<string, number>
}

interface AppMemTracker {
	registerSnapshotProvider?: (name: string, provider: PerfSnapshotProvider) => void
}

interface WebGPUTextureManagerStats {
	numBytesUsed?: number
	numBytesAllocated?: number
	numUsedTextures?: number
	numFreeTextures?: number
	getNumUsedTextures?: () => number
	getNumFreeTextures?: () => number
	freeTextures?: Map<string, GPUTexture[]>
}

interface WebGPUBufferManagerStats {
	numBytesUsed?: number
	numBytesAllocated?: number
	numUsedBuffers?: number
	numFreeBuffers?: number
	getNumUsedBuffers?: () => number
	getNumFreeBuffers?: () => number
	freeBuffers?: Map<string, GPUBuffer[]>
}

interface WebGPUBackendResourceManagers {
	textureManager?: WebGPUTextureManagerStats
	bufferManager?: WebGPUBufferManagerStats
}

export interface TfjsWebgpuFreeResourcesTrimResult {
	supported: boolean
	texturesDestroyed: number
	textureBytesReleased: number
	buffersDestroyed: number
	bufferBytesReleased: number
	destroyFailures: number
}

let tfjsWebgpuPerfProviderRegistered = false

function registerTfjsWebgpuPerfProvider() {
	if (tfjsWebgpuPerfProviderRegistered) return

	const tracker = (globalThis as typeof globalThis & { __appMemTracker?: AppMemTracker })
		.__appMemTracker
	if (typeof tracker?.registerSnapshotProvider !== "function") return

	tracker.registerSnapshotProvider("tfjs-webgpu", () => {
		const backend = tf.backend()
		if (!(backend instanceof WebGPUBackend)) return {}

		const { textureManager, bufferManager } =
			backend as unknown as WebGPUBackendResourceManagers
		if (!textureManager && !bufferManager) return {}

		const usedTextures =
			textureManager?.getNumUsedTextures?.() ?? textureManager?.numUsedTextures ?? 0
		const freeTextures =
			textureManager?.getNumFreeTextures?.() ?? textureManager?.numFreeTextures ?? 0
		const usedTextureBytes = textureManager?.numBytesUsed ?? 0
		const allocatedTextureBytes = textureManager?.numBytesAllocated ?? 0
		const usedBuffers =
			bufferManager?.getNumUsedBuffers?.() ?? bufferManager?.numUsedBuffers ?? 0
		const freeBuffers =
			bufferManager?.getNumFreeBuffers?.() ?? bufferManager?.numFreeBuffers ?? 0
		const usedBufferBytes = bufferManager?.numBytesUsed ?? 0
		const allocatedBufferBytes = bufferManager?.numBytesAllocated ?? 0

		return {
			totals: {
				webgpuTfjsUsedTextureBytes: usedTextureBytes,
				webgpuTfjsFreeTextureBytes: Math.max(0, allocatedTextureBytes - usedTextureBytes),
				webgpuTfjsAllocatedTextureBytes: allocatedTextureBytes,
				webgpuTfjsUsedBufferBytes: usedBufferBytes,
				webgpuTfjsFreeBufferBytes: Math.max(0, allocatedBufferBytes - usedBufferBytes),
				webgpuTfjsAllocatedBufferBytes: allocatedBufferBytes,
			},
			alive: {
				webgpuTfjsUsedTextures: usedTextures,
				webgpuTfjsFreeTextures: freeTextures,
				webgpuTfjsUsedBuffers: usedBuffers,
				webgpuTfjsFreeBuffers: freeBuffers,
			},
		}
	})
	tfjsWebgpuPerfProviderRegistered = true
}

function destroyResources(resources: Iterable<{ destroy: () => void }>) {
	let destroyed = 0
	let failures = 0

	for (const resource of new Set(resources)) {
		try {
			resource.destroy()
			destroyed++
		} catch {
			failures++
		}
	}

	return { destroyed, failures }
}

/**
 * Destroys only resources which TFJS's WebGPU backend has already released to
 * its reuse pools. Live tensors and model-weight buffers remain untouched.
 *
 * This relies on the runtime object layout of tfjs-backend-webgpu 4.22. Keep
 * the pool maps and their keys alive: releaseTexture/releaseBuffer can skip
 * bookkeeping when the manager's free-resource map itself is empty.
 */
export function trimTfjsWebgpuFreeResources(): TfjsWebgpuFreeResourcesTrimResult {
	const emptyResult: TfjsWebgpuFreeResourcesTrimResult = {
		supported: false,
		texturesDestroyed: 0,
		textureBytesReleased: 0,
		buffersDestroyed: 0,
		bufferBytesReleased: 0,
		destroyFailures: 0,
	}

	const backend = tf.backend()
	if (!(backend instanceof WebGPUBackend)) return emptyResult

	const { textureManager, bufferManager } = backend as unknown as WebGPUBackendResourceManagers
	const freeTextures = textureManager?.freeTextures
	const freeBuffers = bufferManager?.freeBuffers
	if (!(freeTextures instanceof Map) && !(freeBuffers instanceof Map)) return emptyResult

	const textureBytesBefore = textureManager?.numBytesAllocated ?? 0
	const bufferBytesBefore = bufferManager?.numBytesAllocated ?? 0
	const textures = freeTextures instanceof Map ? [...freeTextures.values()].flat() : []
	const buffers = freeBuffers instanceof Map ? [...freeBuffers.values()].flat() : []

	// Empty the arrays without clearing the maps. This preserves TFJS's release
	// bookkeeping while ensuring none of the destroyed resources can be reused.
	freeTextures?.forEach((pool) => {
		pool.length = 0
	})
	freeBuffers?.forEach((pool) => {
		pool.length = 0
	})

	if (textureManager) {
		textureManager.numFreeTextures = 0
		if (
			typeof textureManager.numBytesAllocated === "number" &&
			typeof textureManager.numBytesUsed === "number"
		) {
			textureManager.numBytesAllocated = textureManager.numBytesUsed
		}
	}
	if (bufferManager) {
		bufferManager.numFreeBuffers = 0
		if (
			typeof bufferManager.numBytesAllocated === "number" &&
			typeof bufferManager.numBytesUsed === "number"
		) {
			bufferManager.numBytesAllocated = bufferManager.numBytesUsed
		}
	}

	const textureResult = destroyResources(textures)
	const bufferResult = destroyResources(buffers)

	return {
		supported: true,
		texturesDestroyed: textureResult.destroyed,
		textureBytesReleased: Math.max(
			0,
			textureBytesBefore - (textureManager?.numBytesAllocated ?? textureBytesBefore),
		),
		buffersDestroyed: bufferResult.destroyed,
		bufferBytesReleased: Math.max(
			0,
			bufferBytesBefore - (bufferManager?.numBytesAllocated ?? bufferBytesBefore),
		),
		destroyFailures: textureResult.failures + bufferResult.failures,
	}
}

export async function initializeTF() {
	try {
		tf.backend()
		console.warn(`[TF] Backend already initialized: ${tf.getBackend()}`)
		registerTfjsWebgpuPerfProvider()
	} catch (e) {
		const webgpuSupported = isWebGPUSupported()
		const backend = webgpuSupported ? "webgpu" : "webgl"
		console.warn(`[TF] Initializing backend: ${backend} (webgpu supported: ${webgpuSupported})`)

		await tf.setBackend(backend)
		await tf.ready()

		console.warn(`[TF] Backend ready: ${tf.getBackend()}`)
		Sentry.setTag("polarr.tf-backend", tf.getBackend())
		registerTfjsWebgpuPerfProvider()
	}
}

export async function loadGraphModelWithRetry(url: string, options: LoadGraphModelOptions = {}) {
	const { onProgress, streamWeights } = options

	try {
		return await retry(() => tf.loadGraphModel(url, { onProgress, streamWeights }))
	} catch (error) {
		throw new Error(`Failed to load model after 3 attempts: ${url}`, { cause: error })
	}
}
